import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import { Box, Button, Container, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  fetchNotifications,
  markNotificationRead,
  deleteNotification,
  markAllNotificationsRead,
  deleteAllNotifications,
} from '../services/notifications';
import socketClient from '../utils/socketClient';
import notificationPreferences from '../services/notificationPreferences';
import { useAuth } from '../contexts/AuthContext';
import LogoLoader from '../components/Common/LogoLoader';
import SciFiBackground from '../components/SciFiBackground/SciFiBackground';
import Navbar from '../components/Navigation/Navbar';
import PlatformPageHeader from '../components/Common/PlatformPageHeader';

const typeIcon = (type) => {
  const map = {
    bid_placed: '\u{1F4B0}',
    outbid: '\u26A0\uFE0F',
    auction_ended: '\u{1F3C1}',
    listing_approved: '\u2705',
    listing_rejected: '\u274C',
    new_message: '\u{1F4AC}',
    order_confirmed: '\u{1F4E6}',
    new_offer: '\u{1F4DD}',
    offer_accepted: '\u{1F44D}',
    offer_rejected: '\u{1F44E}',
    chat_unlocked: '\u{1F513}',
  };
  return map[type] || '\u{1F514}';
};

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();

  const load = useCallback(async (reset = false) => {
    setLoading(true);
    const res = await fetchNotifications({ page: reset ? 1 : page });
    if (res.success) {
      const list = res.data || [];
      setNotifications((prev) => (reset ? list : [...prev, ...list]));
      setHasMore(res.pagination && page < res.pagination.pages);
    }
    setLoading(false);
  }, [page]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(true); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (page > 1) load(false); }, [page]);

  useEffect(() => {
    const handler = async (notif) => {
      setNotifications((prev) => [notif, ...prev]);
      const canShow = await notificationPreferences.canShowBrowserNotification(notif.type || 'general');
      if (canShow && window.Notification && Notification.permission === 'granted') {
        try {
          new Notification(t('notifications.newNotification'), {
            body: notif.message,
            icon: '/logo192.png',
            tag: `notification-${notif._id || notif.id || Date.now()}`,
          });
        } catch (error) {
          console.error('Failed to show browser notification:', error);
        }
      }
    };
    socketClient.on('notification_new', handler);
    return () => socketClient.off('notification_new', handler);
  }, [t]);

  useEffect(() => {
    const checkAndRequestPermission = async () => {
      if (!isAuthenticated) return;
      const canShow = await notificationPreferences.canShowBrowserNotification('general');
      if (canShow && 'Notification' in window && Notification.permission === 'default') {
        try {
          await Notification.requestPermission();
        } catch (error) {
          console.error('Failed to request notification permission:', error);
        }
      }
    };
    checkAndRequestPermission();
  }, [isAuthenticated]);

  const handleMarkRead = async (id) => {
    await markNotificationRead(id);
    setNotifications((prev) => prev.map((n) => (n._id === id || n.id === id ? { ...n, isRead: true } : n)));
  };

  const handleDelete = async (id) => {
    await deleteNotification(id);
    setNotifications((prev) => prev.filter((n) => (n._id || n.id) !== id));
  };

  const handleMarkAll = async () => {
    await markAllNotificationsRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(t('notifications.deleteAllConfirm'))) return;
    await deleteAllNotifications();
    setNotifications([]);
  };

  const handleClick = (notification) => {
    if (notification.relatedModel === 'AuctionRoom') navigate(`/auction-room/${notification.relatedId}`);
    else if (notification.relatedModel === 'SecondHandProduct') navigate(`/ecommerce/product/${notification.relatedId}`);
    else if (notification.relatedModel === 'SecondHandOffer') navigate(`/offers/${notification.relatedId}`);
    else if (notification.relatedModel === 'Product') navigate(`/ecommerce/product/${notification.relatedId}`);
    else if (notification.relatedModel === 'Message') navigate('/offers/received');
    if (!notification.isRead) handleMarkRead(notification._id || notification.id);
  };

  const unreadCount = notifications.filter((notification) => !notification.isRead).length;

  return (
    <SciFiBackground>
      <Navbar />
      <Box component="main" sx={{ minHeight: 'calc(100vh - 72px)', py: { xs: 5, md: 8 }, px: 2 }}>
        <Container maxWidth="md">
          <PlatformPageHeader
            icon={NotificationsRoundedIcon}
            eyebrow="VITRIA / LIVE UPDATES"
            title={t('notifications.title')}
            subtitle={t('notifications.unread', { count: unreadCount })}
            actions={(
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button variant="outlined" onClick={handleMarkAll}>{t('notifications.markAllRead')}</Button>
                <Button variant="text" color="error" onClick={handleDeleteAll}>{t('notifications.deleteAll')}</Button>
              </Stack>
            )}
          />

          <Stack spacing={1.5}>
            {notifications.length === 0 && !loading && (
              <Paper sx={{ p: { xs: 4, md: 7 }, textAlign: 'center', border: '1px dashed', borderColor: 'divider', bgcolor: 'transparent' }}>
                <NotificationsRoundedIcon sx={{ mb: 2, color: 'primary.light', fontSize: 42 }} />
                <Typography variant="h6">{t('notifications.noNotifications')}</Typography>
              </Paper>
            )}

            {notifications.map((notification) => (
              <Paper
                key={notification._id || notification.id}
                onClick={() => handleClick(notification)}
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  p: { xs: 2, sm: 2.5 },
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: notification.isRead ? 'divider' : alpha(theme.palette.primary.main, 0.34),
                  bgcolor: notification.isRead ? theme.vitria?.input : alpha(theme.palette.primary.main, 0.08),
                  transition: 'transform .2s ease, border-color .2s ease, background-color .2s ease',
                  '&:hover': { transform: 'translateY(-2px)', borderColor: alpha(theme.palette.primary.main, 0.42) },
                })}
              >
                <Box
                  sx={(theme) => ({
                    display: 'grid',
                    flexShrink: 0,
                    width: 44,
                    height: 44,
                    placeItems: 'center',
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    fontSize: 21,
                  })}
                >
                  {typeIcon(notification.type)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
                    <Typography variant="body2" fontWeight={notification.isRead ? 500 : 720}>{notification.message}</Typography>
                    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                      {!notification.isRead && (
                        <Button size="small" variant="text" onClick={(event) => { event.stopPropagation(); handleMarkRead(notification._id || notification.id); }}>
                          {t('notifications.markRead')}
                        </Button>
                      )}
                      <Button size="small" variant="text" color="error" onClick={(event) => { event.stopPropagation(); handleDelete(notification._id || notification.id); }}>
                        {t('delete')}
                      </Button>
                    </Stack>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(notification.timestamp || notification.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Stack>

          {hasMore && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button disabled={loading} variant="outlined" onClick={() => setPage((current) => current + 1)}>
                {loading ? <><LogoLoader size={20} />&nbsp;{t('common.loading')}</> : t('notifications.loadMore')}
              </Button>
            </Box>
          )}
        </Container>
      </Box>
    </SciFiBackground>
  );
};

export default NotificationsPage;
